import { Box, HStack, Skeleton, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { useGetCreditFacilitiesForAssignmentGroupingQuery } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { BalanceSheetImpact } from './BalanceSheetImpact';
import { ConcentrationStats } from './ConcentrationStats';
import { EligibilityStats } from './EligibilityStats';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactsForCreditFacilitiesLazyQueryGQLType } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetAggregatedAssignmentSummaryPreviewQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import { createPortfolioTestStats } from 'packages/loanProduct/ui/v2/AssignmentSummary/TestStats';
import { AssignmentSummaryTopLevelStats } from 'packages/loanProduct/ui/v2/AssignmentSummary/TopLevelStats';
import { parseAggregatedAssignmentSummaryPreview } from 'packages/loanProduct/ui/v2/AssignmentSummary/utils';
import { DownloadPreviewButton } from 'packages/loanProduct/ui/v2/DownloadPreviewButton';
import { EmailServicerReportPreviewsButton } from 'packages/loanProduct/ui/v2/EmailServicerReportPreviewsButton';

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string({ message: 'Missing URL parameter' }),
});

export const AssignmentSummary = ({
  reportingDate,
  fileVersion,
  initialPreviewJobId,
  proposedPreviewJobId,
}: {
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  initialPreviewJobId: string;
  proposedPreviewJobId: string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const [parsedCreditAgreements, setParsedCreditAgreements] = useState<
    CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
  >();
  const [creditFacilitySlugMap, setCreditFacilitySlugMap] = useState<
    Map<string, string>
  >();

  const {
    data: assignmentSummaryPreviews,
    loading: loadingAssignmentSummaryPreviews,
  } = useGetAggregatedAssignmentSummaryPreviewQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'no-cache',
    variables: {
      initialPreviewJobId,
      proposedPreviewJobId,
    },
  });

  const [
    getCurrentCreditAgreementFullArtifacts,
    {
      data: currentCreditAgreementFullArtifacts,
      loading: currentCreditAgreementFullArtifactsLoading,
    },
  ] = useGetCurrentCreditAgreementFullArtifactsForCreditFacilitiesLazyQueryGQLType(
    {
      /**
       *  Programmatically added onError handler.
       * @onError-TODO: Explicitly handle errors for this fetch
       */
      onError: (err) => {
        // TODO: @dmads98 don't use onError, use errors from the useQuery and throw an alert
        // block confirming assignments with CAs don't load
        errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
          ? 'An unexpected error occurred. Please contact Finley for support.'
          : err.message;
        toast(errorToastConfig);
      },
      onCompleted: (data) => {
        const parsedCAs: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[] = [];
        data.currentCreditAgreementFullArtifacts.forEach((artifact) => {
          const {
            creditAgreement,
            id: creditAgreementId,
            creditFacilityId,
          } = artifact;
          finleyAssert(
            creditAgreement && creditAgreementId,
            `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
          );

          let parsedCreditAgreement = null;

          try {
            parsedCreditAgreement = parseCreditAgreementFullArtifact(
              creditAgreement,
            );
          } catch (err) {
            toast({
              ...errorToastConfig,
              id: 'errorGetCurrentCreditAgreementFullArtifact',
              title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
            });
            navigate('/');
          }

          finleyAssert(
            parsedCreditAgreement,
            `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
          );

          parsedCAs.push(parsedCreditAgreement);
        });
        setParsedCreditAgreements(parsedCAs);
      },
    },
  );

  const {
    loading: creditFacilitiesLoading,
    data: creditFacilitiesData,
  } = useGetCreditFacilitiesForAssignmentGroupingQuery({
    variables: { implementationId },
    fetchPolicy: 'network-only',
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: async (data) => {
      getCurrentCreditAgreementFullArtifacts({
        variables: {
          creditFacilityIds: data.creditFacilitiesForAssignmentGrouping.map(
            (creditFacility) => creditFacility.id,
          ),
        },
      });
      const cfSlugMap = new Map<string, string>();
      data.creditFacilitiesForAssignmentGrouping.forEach((creditFacility) => {
        cfSlugMap.set(creditFacility.id, creditFacility.name);
      });
      setCreditFacilitySlugMap(cfSlugMap);
    },
  });

  if (
    creditFacilitiesLoading ||
    !creditFacilitiesData ||
    currentCreditAgreementFullArtifactsLoading ||
    !currentCreditAgreementFullArtifacts ||
    loadingAssignmentSummaryPreviews ||
    !assignmentSummaryPreviews ||
    !assignmentSummaryPreviews.initial ||
    !assignmentSummaryPreviews.proposed ||
    !parsedCreditAgreements ||
    !creditFacilitySlugMap
  ) {
    return (
      <Skeleton
        m="32px 8px 0"
        data-testid="loading-assignment-summary-body"
        height="80vh"
      />
    );
  }

  const initialSummaryPreview = parseAggregatedAssignmentSummaryPreview(
    assignmentSummaryPreviews.initial,
    initialPreviewJobId,
  );
  const proposedSummaryPreview = parseAggregatedAssignmentSummaryPreview(
    assignmentSummaryPreviews.proposed,
    proposedPreviewJobId,
  );

  const initialPortfolioTestStats = createPortfolioTestStats({
    summaryPreview: initialSummaryPreview,
    parsedCreditAgreements,
  });

  const proposedPortfolioTestStats = createPortfolioTestStats({
    summaryPreview: proposedSummaryPreview,
    parsedCreditAgreements,
  });

  return (
    <>
      <Box p="32px" m="32px 0" bg={colors.millet[50]}>
        <HStack mb="32px" w="100%" flexDirection="row-reverse">
          <HStack spacing="16px">
            <EmailServicerReportPreviewsButton
              creditFacilities={
                creditFacilitiesData.creditFacilitiesForAssignmentGrouping
              }
              fileVersion={fileVersion}
              reportingDate={reportingDate}
              fileNamePrefix="extended-receivable-response-tape-preview"
              previewIdentifier={proposedSummaryPreview.previewIdentifier}
            />
            <DownloadPreviewButton
              creditFacilities={
                creditFacilitiesData.creditFacilitiesForAssignmentGrouping
              }
              reportingDate={reportingDate}
              fileVersion={fileVersion}
              previewIdentifier={proposedSummaryPreview.previewIdentifier}
            />
          </HStack>
        </HStack>
        <AssignmentSummaryTopLevelStats
          initial={initialSummaryPreview}
          proposed={proposedSummaryPreview}
          reportingDate={reportingDate}
          fileVersion={fileVersion}
          parsedCreditAgreements={parsedCreditAgreements}
          creditFacilitySlugMap={creditFacilitySlugMap}
        />
        <EligibilityStats
          initialStats={initialPortfolioTestStats}
          proposedStats={proposedPortfolioTestStats}
          parsedCreditAgreements={parsedCreditAgreements}
          creditFacilitySlugMap={creditFacilitySlugMap}
        />
        <ConcentrationStats
          initialStats={initialPortfolioTestStats}
          proposedStats={proposedPortfolioTestStats}
          parsedCreditAgreements={parsedCreditAgreements}
          creditFacilitySlugMap={creditFacilitySlugMap}
        />
      </Box>
      <BalanceSheetImpact
        initialPreview={initialSummaryPreview}
        proposedPreview={proposedSummaryPreview}
        parsedCreditAgreements={parsedCreditAgreements}
        creditFacilitySlugMap={creditFacilitySlugMap}
      />
    </>
  );
};
